import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X, Plus } from 'lucide-react';
import { profilesAPI, assignmentsAPI, authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { hasCap, errMsg } from '../lib/roles';

/** DJ / artist dashboard: invitations, performances, series, own events, availability. */
export default function DJDashboardPage() {
  const { user, loading: authLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [newDate, setNewDate] = useState('');

  const load = useCallback(() => {
    profilesAPI.djDashboard().then(r => setData(r.data)).catch(e => toast.error(errMsg(e)));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth?redirect=/dj'); return; }
    load();
  }, [authLoading, user, load, navigate]);

  const respond = async (a, accept) => {
    try {
      await (accept ? assignmentsAPI.accept(a.assignment_id) : assignmentsAPI.decline(a.assignment_id));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveDates = async (dates) => {
    try {
      const r = await authAPI.updateProfile({ unavailableDates: dates });
      setData(prev => ({ ...prev, unavailable_dates: r.data.unavailable_dates || [] }));
      checkAuth?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="min-h-screen bg-[#050505] pt-24 text-center text-gray-400">Loading…</div>;

  const approved = data.profile.is_dj || hasCap(user, 'dj');
  const Section = ({ title, children }) => (
    <section className="mb-8">
      <h2 className="text-[11px] font-black tracking-widest text-gray-500 mb-3">{title}</h2>
      {children}
    </section>
  );
  const EventRow = ({ e }) => (
    <Link to={`/events/${e.event_id}`} className="flex justify-between gap-3 bg-[#0A0A0A] border border-gray-800 rounded p-3 hover:border-red-500/60">
      <span className="text-white text-sm font-semibold truncate">{e.title}</span>
      <span className="text-gray-500 text-sm whitespace-nowrap">{e.date}{e.city ? ` · ${e.city}` : ''}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#050505] pt-20 pb-16 page-enter">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <h1 className="text-2xl md:text-3xl font-black text-white uppercase">DJ dashboard</h1>
          <Link to={`/artists/${user.user_id || user.userId}`} className="text-sm text-red-400 hover:text-red-300">View my public profile →</Link>
        </div>

        {!approved && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 text-sm rounded p-3 mb-6">
            Your DJ profile is awaiting approval. You can still accept invitations organisers send you.
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[['Upcoming', data.upcoming.length], ['Invitations', data.invitations.length], ['Series', data.series.length], ['Past', data.past.length]].map(([l, v]) => (
            <div key={l} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4"><p className="text-2xl font-black text-white">{v}</p><p className="text-gray-500 text-xs">{l}</p></div>
          ))}
        </div>

        <Section title="INVITATIONS AWAITING ACCEPTANCE">
          {data.invitations.length === 0 ? <p className="text-gray-500 text-sm">No pending invitations.</p> : (
            <div className="space-y-2">
              {data.invitations.map(a => (
                <div key={a.assignment_id} className="flex flex-wrap items-center gap-3 bg-[#0A0A0A] border border-gray-800 rounded p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold">{a.event?.title}{a.scope_type === 'series' ? ' (whole series)' : ''}</p>
                    <p className="text-gray-500 text-xs">
                      {a.scope_type === 'event' && a.event?.date ? `${a.event.date} · ` : ''}{a.event?.city} · from {a.assigned_by?.name}
                      {(data.unavailable_dates || []).includes(a.event?.date) && <span className="text-yellow-400"> · you marked this date unavailable</span>}
                    </p>
                  </div>
                  <button onClick={() => respond(a, false)} className="border border-gray-700 text-gray-200 rounded px-3 py-1.5 text-xs font-bold">Decline</button>
                  <button onClick={() => respond(a, true)} className="btn-primary rounded px-3 py-1.5 text-xs font-bold">Accept</button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="MY UPCOMING PERFORMANCES">
          {data.upcoming.length === 0 ? <p className="text-gray-500 text-sm">Nothing booked yet.</p> : <div className="space-y-2">{data.upcoming.map(e => <EventRow key={e.event_id} e={e} />)}</div>}
        </Section>

        <Section title="MY ASSIGNED SERIES">
          {data.series.length === 0 ? <p className="text-gray-500 text-sm">No series bookings.</p> : (
            <div className="space-y-2">{data.series.map(a => (
              <Link key={a.assignment_id} to={`/team/series/${a.scope_id}`} className="block bg-[#0A0A0A] border border-gray-800 rounded p-3 text-white text-sm font-semibold hover:border-red-500/60">{a.event?.title}</Link>
            ))}</div>
          )}
        </Section>

        <Section title={`MY PAST PERFORMANCES (${data.past.length})`}>
          <div className="space-y-2">{data.past.slice(0, 10).map(e => <EventRow key={e.event_id} e={e} />)}</div>
        </Section>

        {approved && (
          <Section title="MY OWN EVENTS & SERIES">
            <div className="flex flex-wrap gap-2 mb-3">
              <Link to="/organiser" className="btn-primary px-4 py-2 rounded text-sm font-bold">Create / manage events</Link>
              <Link to="/recurring-series" className="border border-gray-700 text-white px-4 py-2 rounded text-sm font-bold">My series</Link>
            </div>
            <div className="space-y-2">{data.own_events.slice(0, 5).map(e => <EventRow key={e.event_id} e={e} />)}</div>
          </Section>
        )}

        <Section title="AVAILABILITY — DATES I'M NOT AVAILABLE">
          <form className="flex gap-2 mb-3" onSubmit={e => { e.preventDefault(); if (newDate) { saveDates([...(data.unavailable_dates || []), newDate].sort()); setNewDate(''); } }}>
            <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="bg-[#111] border border-gray-700 text-white px-3 py-2 rounded text-sm" />
            <button type="submit" className="btn-primary px-3 rounded text-sm font-bold flex items-center gap-1"><Plus size={14} /> Add</button>
          </form>
          <div className="flex flex-wrap gap-2">
            {(data.unavailable_dates || []).length === 0 && <p className="text-gray-500 text-sm">Organisers see a warning if they book you on these dates.</p>}
            {(data.unavailable_dates || []).map(d => (
              <button key={d} onClick={() => saveDates(data.unavailable_dates.filter(x => x !== d))} className="flex items-center gap-1 border border-gray-700 rounded-full px-3 py-1 text-xs text-gray-200 hover:border-red-500">
                {d} <X size={12} />
              </button>
            ))}
          </div>
        </Section>

        {data.collaborators.length > 0 && (
          <Section title="COLLABORATIONS & ORGANISER CONTACTS">
            <div className="flex flex-wrap gap-2">
              {data.collaborators.map(c => <span key={c.user_id} className="border border-gray-800 rounded-full px-3 py-1 text-xs text-gray-200">{c.name}</span>)}
            </div>
          </Section>
        )}

        <Section title="MY PROFILE">
          <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
            <p className="text-gray-300 text-sm">{data.profile.bio || 'Add a biography, photos and music links so organisers can book you.'}</p>
            <Link to="/account" className="text-red-400 text-sm font-bold mt-2 inline-block">Edit profile, social & music links</Link>
          </div>
        </Section>
      </div>
    </div>
  );
}
